import request from "@/utils/request";

/**
 * 用户登录
 * @param {Object} data - 登录数据
 * @param {string} data.account - 账号
 * @param {string} data.password - 密码
 */
export function login(data) {
  return request({
    url: "/user/login",
    method: "post",
    data,
  });
}

/**
 * 微信一键登录
 * @param {Object} data - 微信登录数据
 * @param {string} data.code - 微信授权code
 */
export function wechatLogin(data) {
  return request({
    url: "/user/wechat_login",
    method: "post",
    data,
  });
}

/**
 * 用户注册
 * @param {Object} data - 注册数据
 * @param {string} data.mobile - 手机号（必填）
 * @param {string} data.username - 用户名（必填，通常使用手机号）
 * @param {string} data.password - 密码（必填，6-20位）
 * @param {string} data.code - 短信验证码（必填）
 * @param {string} data.email - 邮箱（可选）
 */
export function register(data) {
  return request({
    url: "/user/register",
    method: "post",
    data,
  });
}

/**
 * 获取用户信息
 */
export function getUserInfo() {
  return request({
    url: "/user/info",
    method: "get",
  });
}

/**
 * 更新用户信息
 * @param {Object} data - 用户信息数据
 * @param {string} data.nickname - 用户昵称（可选，最大长度20个字符）
 * @param {string} data.phone - 手机号（可选，需符合手机号格式：1[3-9]\d{9}）
 * @param {string} data.email - 邮箱地址（可选，需符合邮箱格式）
 * @param {string} data.idCard - 身份证号（可选，需符合18位身份证号格式）
 * @param {string} data.bio - 个人简介（可选）
 * @param {string} data.avatar - 头像URL（可选，通过上传接口获得）
 * @returns {Promise} 返回更新后的用户信息
 */
export function updateUserInfo(data) {
  return request({
    url: "/user/update",
    method: "put",
    data,
  });
}

/**
 * 修改密码
 * @param {Object} data - 密码数据
 * @param {string} data.oldPassword - 旧密码
 * @param {string} data.newPassword - 新密码
 */
export function changePassword(data) {
  return request({
    url: "/user/password",
    method: "post",
    data,
  });
}

/**
 * 退出登录
 */
export function logout() {
  return request({
    url: "/user/logout",
    method: "post",
  });
}

/**
 * 发送验证码（注册场景）
 * @param {string} phone - 手机号
 */
export function sendCode(phone) {
  return request({
    url: "/user/send_code",
    method: "post",
    data: { phone },
  });
}

// ==================== 课程相关接口 ====================

/**
 * 获取课程列表
 */
export function getCourseList(params) {
  return request({
    url: "/course/list",
    method: "get",
    params,
  });
}

/**
 * 获取课程详情
 * @param {string|number} course_id - 课程ID
 */
export function getCourseDetail(course_id) {
  return request({
    url: "/course/detail",
    method: "get",
    params: { course_id },
  });
}

/**
 * 获取特色技能分类列表
 */
export function getSkillCategories() {
  return request({
    url: "/course/skillCategories",
    method: "get",
  });
}

// ==================== 考试相关接口 ====================

/**
 * 获取考试列表
 */
export function getExamList(params) {
  return request({
    url: "/exam/list",
    method: "get",
    params,
  });
}

/**
 * 获取考试详情
 * @param {string|number} id - 考试ID
 */
export function getExamDetail(id) {
  return request({
    url: "/exam/detail",
    method: "get",
    params: { id }
  });
}

// ==================== 证书相关接口 ====================

/**
 * 获取证书列表
 */
export function getCertificateList(params) {
  return request({
    url: "/certificate/list",
    method: "get",
    params,
  });
}

/**
 * 获取证书详情
 */
export function getCertificateDetail(id) {
  return request({
    url: `/certificate/detail/${id}`,
    method: "get",
  });
}

/**
 * 提交证书验证
 * @param {Object} data - 验证数据
 * @param {string} data.name - 查询人姓名，可空
 * @param {string} data.phone - 查询人联系方式，可空
 * @param {string} data.cert_name - 证书名称，可空
 * @param {string} data.cert_number - 证书编号（必填）
 * @param {string} data.image_url - 证书图片URL，可空
 */
export function verifyCertificate(data) {
  return request({
    url: "/certificate/verify",
    method: "post",
    data,
  });
}

/**
 * 获取我的验证历史（登录）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getCertificateHistory(params) {
  return request({
    url: "/certificate/history",
    method: "get",
    params,
  });
}

/**
 * 获取证书分类列表
 */
export function getCertificateCategoryList() {
  return request({
    url: "/certificate/categoryList",
    method: "get",
  });
}

/**
 * 根据分类获取验证地址列表
 * @param {string|number} categoryId - 分类ID
 */
export function getCertificateVerifyUrls(categoryId) {
  return request({
    url: "/certificate/verifyUrlList",
    method: "get",
    params: { category_id: categoryId },
  });
}

// ==================== 公共接口 ====================

/**
 * 加载初始化
 * @param {Object} data - 初始化数据
 * @param {string} data.version - 版本号（必填）
 * @param {string} data.lng - 经度（必填）
 * @param {string} data.lat - 纬度（必填）
 */
export function initApp(data) {
  return request({
    url: "/common/init",
    method: "get",
    params: data,
  });
}

/**
 * 上传文件
 * @param {FormData} formData - 文件数据，包含file字段
 */
export function uploadFile(formData) {
  return request({
    url: "/common/upload",
    method: "post",
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

/**
 * 获取验证码
 * @param {string} id - 要生成验证码的标识（必填）
 */
export function getCaptcha(id) {
  return request({
    url: "/common/captcha",
    method: "get",
    params: { id },
    responseType: 'blob'
  });
}

// ==================== 课程相关接口 ====================

/**
 * 获取课程大纲（章节+课时）
 * @param {string|number} course_id - 课程ID
 */
export function getCourseOutline(course_id) {
  return request({
    url: "/course/outline",
    method: "get",
    params: { course_id }
  });
}

/**
 * 获取我的课程（登录）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.limit - 每页数量
 */
export function getMyCourses(params) {
  return request({
    url: "/course/my",
    method: "get",
    params,
  });
}

// ==================== 考试相关接口 ====================

/**
 * 提交考试报名（登录）
 * @param {Object} data - 报名数据
 */
export function applyExam(data) {
  return request({
    url: "/exam/apply",
    method: "post",
    data,
  });
}

/**
 * 获取我的报名（登录）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getMyExamApplications(params) {
  return request({
    url: "/exam/my",
    method: "get",
    params,
  });
}

/**
 * 下载准考证
 * @param {Object} params - 下载参数
 * @param {string|number} params.ticket_id - 准考证ID
 * @param {string} params.id_number - 身份证号码
 */
export function downloadExamTicket(params) {
  return request({
    url: "/exam/downloadTicket",
    method: "get",
    params,
    responseType: 'blob'
  });
}

/**
 * 查询准考证
 * @param {Object} params - 查询参数
 * @param {string|number} params.ticket_id - 准考证ID
 * @param {string} params.id_number - 身份证号码
 */
export function queryExamTicket(params) {
  return request({
    url: "/exam/searchTicket",
    method: "get",
    params
  });
}

/**
 * 获取报名详情（登录）
 * @param {string|number} id - 报名ID
 */
export function getExamApplicationDetail(id) {
  return request({
    url: "/exam/applicationDetail",
    method: "get",
    params: { id }
  });
}

// ==================== 职位相关接口 ====================

/**
 * 获取职位列表
 */
export function getJobList(params) {
  return request({
    url: "/job/list",
    method: "get",
    params,
  });
}

/**
 * 获取职位详情
 * @param {string|number} id - 职位ID
 */
export function getJobDetail(id) {
  return request({
    url: "/job/detail",
    method: "get",
    params: { job_id: id }
  });
}

/**
 * 投递职位（登录）
 * @param {Object} data - 投递数据
 */
export function applyJob(data) {
  return request({
    url: "/job/apply",
    method: "post",
    data,
  });
}

/**
 * 获取我的投递（登录）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getMyJobApplications(params) {
  return request({
    url: "/job/my",
    method: "get",
    params,
  });
}

/**
 * 获取职位城市列表
 */
export function getJobLocations() {
  return request({
    url: "/job/locations",
    method: "get",
  });
}

// ==================== 学习进度接口（需登录） ====================

/**
 * 获取课程学习进度
 * @param {string|number} courseId - 课程ID
 */
export function getCourseProgress(courseId) {
  return request({
    url: "/learning/progress",
    method: "get",
    params: { course_id: courseId }
  });
}

/**
 * 同步学习进度（上报当前时间点）
 * @param {Object} data - 进度数据
 */
export function syncLearningProgress(data) {
  return request({
    url: "/learning/sync",
    method: "post",
    data,
  });
}

/**
 * 标记课时完成
 * @param {Object} data - 完成数据
 */
export function markLessonComplete(data) {
  return request({
    url: "/learning/complete",
    method: "post",
    data,
  });
}

/**
 * 保存学习进度
 * @param {Object} data - 进度数据
 * @param {string|number} data.lesson_id - 课时ID
 * @param {number} data.current_time - 当前播放时间（秒）
 */
export function saveLearningProgress(data) {
  return request({
    url: "/learning/save",
    method: "post",
    data,
  });
}

// ==================== 站内消息接口（需登录） ====================

/**
 * 获取消息列表
 * @param {Object} params - 查询参数
 */
export function getMessages(params) {
  return request({
    url: "/message/list",
    method: "get",
    params,
  });
}

/**
 * 标记消息已读
 * @param {string|number} id - 消息ID
 */
export function markMessageRead(id) {
  return request({
    url: `/message/read/${id}`,
    method: "post",
  });
}

/**
 * 全部标记已读
 */
export function markAllMessagesRead() {
  return request({
    url: "/message/readAll",
    method: "post",
  });
}

// ==================== 订单/支付接口 ====================

/**
 * 创建订单（登录）
 * @param {Object} data - 订单数据
 */
export function createOrder(data) {
  return request({
    url: "/order/create",
    method: "post",
    data,
  });
}

/**
 * 获取订单详情（登录）
 * @param {string|number} id - 订单ID
 */
export function getOrderDetail(id) {
  return request({
    url: `/order/${id}`,
    method: "get",
  });
}

/**
 * 取消订单（登录）
 * @param {string} order_no - 订单号
 */
export function cancelOrder(order_no) {
  return request({
    url: "/order/cancel",
    method: "post",
    data: { order_no }
  });
}

/**
 * 查询支付状态（公开）
 * @param {string} orderNo - 订单号
 */
export function getPaymentStatus(orderNo) {
  return request({
    url: "/order/checkStatus",
    method: "get",
    params: {
      order_no: orderNo
    }
  });
}

/**
 * 发起支付
 * @param {Object} data - 支付数据
 * @param {string} data.order_no - 订单号（必填）
 * @param {string} data.pay_channel - 支付渠道：web|wap|mp|miniapp|scan，默认自动识别
 * @param {string} data.wechat_code - 微信授权code（可选）
 */
export function payOrder(data) {
  return request({
    url: "/order/pay",
    method: "post",
    data,
  });
}

/**
 * 获取我的订单列表（登录）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getMyOrders(params) {
  return request({
    url: "/order/myOrders",
    method: "get",
    params,
  });
}

// ==================== 人才相关接口 ====================

/**
 * 获取人才列表
 */
export function getTalentList(params) {
  return request({
    url: "/talent/list",
    method: "get",
    params,
  });
}

/**
 * 获取人才详情
 * @param {string|number} id - 人才ID
 */
export function getTalentDetail(id) {
  return request({
    url: "/talent/detail",
    method: "get",
    params: { id }
  });
}

/**
 * 提交人才入库申请
 * @param {Object} data - 人才申请数据
 * @param {string} data.name - 姓名（必填）
 * @param {string} data.gender - 性别，可空
 * @param {string} data.phone - 手机号（必填）
 * @param {string} data.email - 邮箱，可空
 * @param {string} data.profession - 职业，可空
 * @param {string} data.education - 学历，可空
 * @param {string} data.work_exp - 工作经验，可空
 * @param {string} data.skills - 技能，逗号分隔，可空
 * @param {string} data.certificates - 证书，逗号分隔，可空
 * @param {string} data.introduction - 个人介绍，可空
 * @param {string} data.cert_images - 证书图片，逗号分隔或JSON，可空
 */
export function submitTalentApplication(data) {
  return request({
    url: "/talent/apply",
    method: "post",
    data,
  });
}

/**
 * 获取我的人才档案（查询）
 */
export function getMyTalentProfile() {
  return request({
    url: "/talent/me",
    method: "get",
  });
}

/**
 * 更新我的人才档案（新增或编辑）
 * @param {Object} data - 人才档案数据
 * @param {number} data.type_id - 分类ID（必填）
 * @param {string} data.name - 姓名（必填）
 * @param {string} data.gender - 性别，可选
 * @param {string} data.phone - 手机号（必填）
 * @param {string} data.email - 邮箱，可选
 * @param {string} data.profession - 专业或职业，可选
 * @param {string} data.education - 学历，可选
 * @param {string} data.work_exp - 工作经验，可选
 * @param {string} data.skills - 专业技能，可选
 * @param {string} data.certificates - 证书，可选
 * @param {string} data.introduction - 个人简介，可选
 * @param {string} data.cert_images - 证书照片（JSON字符串或逗号分隔），可选
 */
export function updateTalentProfile(data) {
  return request({
    url: "/talent/update",
    method: "post",
    data,
  });
}

// ==================== 分类类型接口 ====================

/**
 * 获取分类类型列表（课程分类）
 * @param {Object} params - 查询参数
 * @param {string} params.category - 分类类型（可选，如：global）
 */
export function getTypeList(params) {
  return request({
    url: "/index/type",
    method: "get",
    params,
  });
}

/**
 * 获取人才分类列表
 */
export function getTalentCategories() {
  return request({
    url: "/talent/categories",
    method: "get",
  });
}

// ==================== 首页Banner接口 ====================

/**
 * 获取首页Banner列表
 */
export function getBannerList() {
  return request({
    url: "/index/banner",
    method: "get",
  });
}

// ==================== 简历上传接口（需登录） ====================

/**
 * 上传简历附件
 * @param {Object} data - 包含简历URL的对象
 * @param {string} data.resume_url - 简历文件URL（必填）
 */
export function resumeUpload(data) {
  return request({
    url: "/job/resumeUpload",
    method: "post",
    data
  });
}

/**
 * 查询我的简历
 */
export function resumeCheck() {
  return request({
    url: "/job/resumeCheck",
    method: "get",
  });
}

/**
 * 删除简历
 */
export function resumeDelete() {
  return request({
    url: "/job/resumeDelete",
    method: "post",
  });
}

// ==================== 帮助中心接口 ====================

/**
 * 获取帮助中心类型列表
 */
export function getHelpTypes() {
  return request({
    url: "/help/types",
    method: "get",
  });
}

/**
 * 获取帮助中心分类列表
 */
export function getHelpCategories() {
  return request({
    url: "/help/categories",
    method: "get",
  });
}

/**
 * 获取帮助中心内容详情
 * @param {string} type - 类型：faq/guide/payment/refund/contact
 */
export function getHelpDetail(type) {
  return request({
    url: "/help/detail",
    method: "get",
    params: { type }
  });
}

// ==================== 公告相关接口 ====================

/**
 * 获取公告列表
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getNoticeList(params) {
  return request({
    url: "/index/noticeList",
    method: "get",
    params
  });
}

/**
 * 获取公告详情
 * @param {string|number} id - 公告ID
 */
export function getNoticeDetail(id) {
  return request({
    url: "/index/noticeDetail",
    method: "get",
    params: { id }
  });
}

/**
 * 获取报考专业列表
 */
export function getMajorList() {
  return request({
    url: "/index/majorList",
    method: "get"
  });
}

/**
 * 提交证书邮寄信息
 * @param {Object} data - 邮寄信息数据
 * @param {string} data.cert_name - 证书上的姓名（必填）
 * @param {string|number} data.major_id - 证书上的专业ID（必填）
 * @param {string} data.recipient_name - 收件人姓名（必填）
 * @param {string} data.recipient_phone - 收件人电话（必填）
 * @param {string} data.recipient_address - 收件人地址（必填）
 */
export function submitCertificateMailing(data) {
  return request({
    url: "/certificate_mail/submit",
    method: "post",
    data
  });
}

/**
 * 获取我的证书邮递列表
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 */
export function getMyCertificateMailing(params) {
  return request({
    url: "/certificate_mail/lists",
    method: "get",
    params
  });
}

// ==================== 工具相关接口 ====================

/**
 * 获取工具分类列表
 */
export function getToolCategories() {
  return request({
    url: "/tool/categories",
    method: "get"
  });
}

/**
 * 获取工具列表
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.pagesize - 每页数量
 * @param {number} params.type_id - 工具分类ID（可选，不传则获取全部）
 */
export function getToolList(params) {
  return request({
    url: "/tool/list",
    method: "get",
    params
  });
}

/**
 * 获取工具详情
 * @param {string|number} id - 工具ID
 */
export function getToolDetail(id) {
  return request({
    url: "/tool/detail",
    method: "get",
    params: { id }
  });
}

// ==================== 题库相关接口 ====================

/**
 * 获取某专业下的试卷列表
 * @param {Object} params - 查询参数
 * @param {number} params.major_id - 专业ID（可选）
 * @param {number} params.type_id - 分类ID（可选）
 * @param {number} params.page - 页码（可选）
 * @param {number} params.limit - 每页数量（可选）
 */
export function getQuestionPapers(params) {
  return request({
    url: "/question/papers",
    method: "get",
    params
  });
}

/**
 * 获取题库列表（支持分页）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.limit - 每页数量
 * @param {number} params.type_id - 分类ID
 */
export function getQuizList(params) {
  return request({
    url: "/question/papers",
    method: "get",
    params
  });
}

/**
 * 获取题库详情
 * @param {string|number} id - 题库/试卷ID
 */
export function getQuizDetail(id) {
  return request({
    url: "/question/paperDetail",
    method: "get",
    params: { id }
  });
}

/**
 * 获取试卷下的题目列表（含选项）
 * @param {Object} params - 查询参数
 * @param {number} params.paper_id - 试卷ID（必填）
 */
export function getQuestionList(params) {
  return request({
    url: "/question/list",
    method: "get",
    params
  });
}

/**
 * 提交答题记录与成绩统计
 * @param {Object} data - 答题数据
 * @param {number} data.paper_id - 试卷ID（必填）
 * @param {Array} data.answers - 用户的答题数据数组，格式如：[{question_id: 1, answer: 'A'}]
 */
export function submitQuestionAnswers(data) {
  return request({
    url: "/question/submit",
    method: "post",
    data
  });
}

/**
 * 提交试卷答题结果（支持做题/背题模式）
 * @param {Object} data - 答题数据
 * @param {number} data.paper_id - 试卷ID
 * @param {number} data.type - 模式类型：1=做题, 2=背题
 * @param {Array} data.answers - 答题数据数组，格式如：[{question_id: 1, answer: 'A'}]
 */
export function submitQuestionPaper(data) {
  return request({
    url: "/question/submit",
    method: "post",
    data
  });
}

